// src/components/facturacion/ModalCorregirFactura.js
// =========================================================================
// CORREGIR UNA FACTURA QUE HACIENDA NO ACEPTÓ (VERI*FACTU)
// Si Verifacti la rechazó (normalmente porque el NIF o el nombre del cliente
// no coinciden con los de Hacienda), la factura no llegó a registrarse: se
// corrigen los datos del cliente, o se pasa a simplificada, y se reenvía con
// el mismo número.
// =========================================================================
import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Modal } from '../common/ui';

const entrada = 'w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15';

export default function ModalCorregirFactura({ factura, onClose, onCorregida }) {
    const [tipo, setTipo] = useState('F1');
    const [cliente, setCliente] = useState({ nif: '', nombre: '', domicilio: '' });
    const [error, setError] = useState('');
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        setTipo(factura?.tipoFactura === 'F2' ? 'F2' : 'F1');
        setCliente({
            nif: factura?.destinatario?.nif || '',
            nombre: factura?.destinatario?.nombre || '',
            domicilio: factura?.destinatario?.domicilio || ''
        });
        setError('');
    }, [factura]);

    if (!factura) return null;

    const enviar = async () => {
        const nif = cliente.nif.replace(/[\s-]/g, '').toUpperCase();
        if (tipo === 'F1' && (nif.length !== 9 || cliente.nombre.trim().length < 2)) {
            setError('Indica el NIF (9 caracteres) y el nombre tal como figuran en Hacienda.');
            return;
        }
        setEnviando(true);
        setError('');
        try {
            const dto = await api.put(`/verifactu/facturas/${factura.id}/corregir`, {
                tipoFactura: tipo,
                ...(tipo === 'F1' && {
                    destinatario: {
                        nif,
                        nombre: cliente.nombre.trim(),
                        ...(cliente.domicilio.trim() && { domicilio: cliente.domicilio.trim() })
                    }
                })
            });
            if (dto?.verifactu?.estado === 'rechazada') {
                setError(`Hacienda la ha vuelto a rechazar: ${dto.verifactu.mensajeError || 'revisa los datos.'}`);
                onCorregida?.(dto, false);
                return;
            }
            onCorregida?.(dto, true);
        } catch (err) {
            setError(err.message || 'No se pudo reenviar la factura.');
        } finally {
            setEnviando(false);
        }
    };

    const opcion = (valor, titulo, detalle) => (
        <button type="button" onClick={() => setTipo(valor)}
                className={`text-left p-3 rounded-xl border-2 transition-all ${tipo === valor ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
            <span className="block text-sm font-black text-slate-800">{titulo}</span>
            <span className="block text-xs font-semibold text-slate-500">{detalle}</span>
        </button>
    );

    return (
        <Modal open onClose={onClose} titulo={`Corregir y reenviar · ${factura.numeroFactura}`}
               subtitulo="Hacienda no llegó a registrarla: se reenvía con el mismo número." size="lg">
            <div className="space-y-5">
                {factura.verifactu?.mensajeError && (
                    <p className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-xs font-semibold text-rose-800">
                        <span className="font-black">Motivo del rechazo:</span> {factura.verifactu.mensajeError}
                    </p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {opcion('F1', 'Completa (con NIF)', 'Con los datos del cliente corregidos.')}
                    {opcion('F2', 'Simplificada (ticket)', 'Sin datos fiscales del cliente.')}
                </div>

                {tipo === 'F1' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="block mb-1 text-[11px] font-bold text-slate-500">NIF / DNI</span>
                            <input className={entrada} value={cliente.nif} onChange={(e) => setCliente(c => ({ ...c, nif: e.target.value }))} />
                        </label>
                        <label className="block">
                            <span className="block mb-1 text-[11px] font-bold text-slate-500">Nombre o razón social (como en Hacienda)</span>
                            <input className={entrada} value={cliente.nombre} onChange={(e) => setCliente(c => ({ ...c, nombre: e.target.value }))} />
                        </label>
                        <label className="block sm:col-span-2">
                            <span className="block mb-1 text-[11px] font-bold text-slate-500">Domicilio (opcional)</span>
                            <input className={entrada} value={cliente.domicilio} onChange={(e) => setCliente(c => ({ ...c, domicilio: e.target.value }))} />
                        </label>
                    </div>
                )}

                {error && <p className="text-sm font-bold text-rose-700">{error}</p>}

                <div className="flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl">Cancelar</button>
                    <button onClick={enviar} disabled={enviando}
                            className="px-5 py-2.5 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl disabled:opacity-50">
                        {enviando ? 'Enviando…' : 'Corregir y reenviar'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
